import { useCallback, useEffect, useRef, useState } from 'react'
import { CheckCircle2, CircleAlert, Smartphone } from 'lucide-react'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { useConnectWhatsApp, useWhatsAppConnection, type WhatsAppAccount, type WhatsAppNumber } from '../api/whatsapp'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

// Meta's JavaScript SDK, loaded only here and only when the owner presses
// Connect (ADR-0014). The pop-up ends with a code (the login callback) and
// the chosen account and number (a message event from facebook.com).
type FBLoginResponse = { authResponse?: { code?: string } | null; status?: string }
type FBSDK = {
  init: (o: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }) => void
  login: (cb: (r: FBLoginResponse) => void, o: Record<string, unknown>) => void
}
declare global {
  interface Window {
    FB?: FBSDK
    fbAsyncInit?: () => void
  }
}

function loadSDK(appId: string, version: string): Promise<FBSDK> {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB)
      return
    }
    window.fbAsyncInit = () => {
      if (!window.FB) return
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version })
      resolve(window.FB)
    }
    const s = document.createElement('script')
    s.src = 'https://connect.facebook.net/en_US/sdk.js'
    s.async = true
    s.onerror = () => reject(new Error("Meta's sign-up script could not be loaded. Check the connection or an ad blocker, then try again."))
    document.body.appendChild(s)
  })
}

type SignupEvent = { waba_id?: string; phone_number_id?: string; current_step?: string; error_message?: string }

// The connection page: what is connected, and the button that connects.
export function WhatsAppPage() {
  const me = useMe()
  const conn = useWhatsAppConnection()
  const connect = useConnectWhatsApp()
  const owner = me.data?.user.role === 'owner'
  const [popupError, setPopupError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Filled by the message event before the login callback fires.
  const session = useRef<SignupEvent>({})

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!/^https:\/\/(www|web|business)\.facebook\.com$/.test(e.origin) || typeof e.data !== 'string') return
      try {
        const msg = JSON.parse(e.data) as { type?: string; event?: string; data?: SignupEvent }
        if (msg.type !== 'WA_EMBEDDED_SIGNUP') return
        if (msg.event === 'CANCEL') {
          setPopupError(msg.data?.current_step ? `Sign-up was closed at the step "${msg.data.current_step}". Nothing was changed.` : 'Sign-up was closed. Nothing was changed.')
          setBusy(false)
        } else if (msg.event === 'ERROR') {
          setPopupError(msg.data?.error_message ? `Meta reported: ${msg.data.error_message}` : 'Meta reported an error in the sign-up pop-up.')
          setBusy(false)
        } else {
          session.current = msg.data ?? {}
        }
      } catch {
        // Not the sign-up event.
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const signup = conn.data?.signup
  const start = useCallback(async () => {
    if (!signup) return
    setPopupError(null)
    connect.reset()
    session.current = {}
    setBusy(true)
    try {
      const fb = await loadSDK(signup.appId, signup.graphVersion)
      fb.login(
        (r) => {
          const code = r.authResponse?.code
          const wabaId = session.current.waba_id
          if (!code) {
            setPopupError('Sign-up was closed before it finished. Nothing was changed.')
            setBusy(false)
            return
          }
          if (!wabaId) {
            setPopupError('Meta did not say which WhatsApp account was chosen. Close this and try again.')
            setBusy(false)
            return
          }
          const body = session.current.phone_number_id ? { code, wabaId, phoneNumberId: session.current.phone_number_id } : { code, wabaId }
          connect.mutate(body, { onSettled: () => setBusy(false) })
        },
        {
          config_id: signup.configurationId,
          response_type: 'code',
          override_default_response_type: true,
          extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
        },
      )
    } catch (err) {
      setPopupError(err instanceof Error ? err.message : String(err))
      setBusy(false)
    }
  }, [signup, connect])

  const error = connect.error as RequestFailed | null

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-6 sm:py-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">WhatsApp</h1>
        <p className="text-sm text-muted-foreground">
          The WhatsApp Business account and numbers this workspace sends and receives with. Meta holds the account; wattool is connected to it.
        </p>
      </div>

      {conn.isPending ? (
        <div role="status" aria-label="Loading the connection" className="flex flex-col gap-3">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : conn.isError ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(conn.error as RequestFailed).message}</p>
      ) : (
        <>
          {conn.data.accounts.length === 0 ? (
            <div className="flex flex-col gap-3 rounded-xl border bg-card p-4">
              <div className="flex items-center gap-2 font-semibold"><Smartphone className="size-5 text-muted-foreground" aria-hidden="true" />No WhatsApp account connected yet</div>
              <p className="text-sm text-muted-foreground">
                Connecting opens Meta's sign-up in a pop-up. Have ready: a Facebook login that manages the business, the business name and address, and a phone number that can receive a code (one not on a personal WhatsApp, or the WhatsApp Business app number to keep using both).
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              {conn.data.accounts.map((a) => <AccountCard key={a.id} account={a} />)}
            </ul>
          )}

          {connect.isSuccess && (
            <div role="status" className="flex flex-col gap-2 rounded-lg bg-primary-soft p-3 text-sm text-primary-text">
              <span className="flex items-center gap-2 font-semibold"><CheckCircle2 className="size-4" aria-hidden="true" />Connected {connect.data.account.name}.</span>
              {connect.data.warnings.length === 0 ? (
                <span>Messages to its numbers now arrive in the inbox. Next: Templates, to sync or create the first template.</span>
              ) : (
                <ul className="list-disc pl-5">
                  {connect.data.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              )}
            </div>
          )}
          {error && <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{error.message}</p>}
          {popupError && <p role="alert" className="rounded-lg bg-attention-soft p-3 text-sm text-attention-text">{popupError}</p>}

          {owner ? (
            signup ? (
              <div className="flex flex-col gap-2">
                <div>
                  <Button type="button" onClick={() => void start()} disabled={busy || connect.isPending}>
                    {connect.isPending ? 'Connecting' : busy ? 'Waiting for the pop-up' : conn.data.accounts.length === 0 ? 'Connect WhatsApp' : 'Connect again'}
                  </Button>
                </div>
                {conn.data.accounts.length > 0 && (
                  <p className="text-xs text-muted-foreground">Connect again after changing the account at Meta, or if a step above did not finish. It repeats every step and keeps the messages already here.</p>
                )}
              </div>
            ) : (
              <p className="flex items-start gap-2 rounded-lg bg-attention-soft p-3 text-sm text-attention-text">
                <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                Self-serve connection is not set up on this server yet. Ask the operator to connect the account.
              </p>
            )
          ) : (
            <p className="text-sm text-muted-foreground">Only the owner can connect or change the WhatsApp account.</p>
          )}
        </>
      )}
    </main>
  )
}

const quality: Record<WhatsAppNumber['qualityRating'], { label: string; variant: 'success' | 'attention' | 'danger' | 'neutral' }> = {
  green: { label: 'Quality good', variant: 'success' },
  yellow: { label: 'Quality medium', variant: 'attention' },
  red: { label: 'Quality low', variant: 'danger' },
  unknown: { label: 'Quality not read yet', variant: 'neutral' },
}

function limitWords(tier: string): string {
  const m = /^TIER_(\d+)(K?)$/.exec(tier)
  if (tier === 'TIER_UNLIMITED') return 'Unlimited customers a day'
  if (!m) return 'Daily limit not read yet'
  return `${m[2] ? `${m[1]},000` : m[1]} customers a day`
}

function AccountCard({ account }: { account: WhatsAppAccount }) {
  const disconnected = account.status !== 'connected'
  return (
    <li className="flex flex-col gap-3 rounded-xl border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{account.name || 'WhatsApp Business account'}</span>
        <span className="text-xs text-muted-foreground">{account.metaWabaId}</span>
        {disconnected ? <Badge variant="destructive">{account.status === 'restricted' ? 'Restricted by Meta' : 'Disconnected'}</Badge> : <Badge variant="success">Connected</Badge>}
        {account.webhookSubscribed ? null : <Badge variant="neutral">Webhooks not confirmed by wattool</Badge>}
      </div>
      {account.numbers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No phone number under this account yet.</p>
      ) : (
        <ul className="divide-y">
          {account.numbers.map((n) => (
            <li key={n.id} className="flex flex-col gap-1 py-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-medium">{n.displayNumber}</span>
                <span className="text-sm text-muted-foreground">{n.verifiedName}</span>
                {n.status !== 'active' && <Badge variant="destructive">{n.status === 'paused' ? 'Paused by Meta' : 'Disconnected'}</Badge>}
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                <Badge variant={quality[n.qualityRating].variant}>{quality[n.qualityRating].label}</Badge>
                <Badge variant="neutral">{limitWords(n.messagingLimit)}</Badge>
                {n.registered ? <Badge variant="neutral">Ready to send</Badge> : <Badge variant="neutral">Registration not confirmed by wattool</Badge>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
